export const baseUrl = 'https://api.loyaltybridge.io';

export const docSections = [
  { id: 'quickstart', label: 'Quickstart', icon: 'ri-rocket-line' },
  { id: 'authentication', label: 'Authentication', icon: 'ri-key-2-line' },
  { id: 'endpoints', label: 'API reference', icon: 'ri-code-s-slash-line' },
  { id: 'errors', label: 'Errors & limits', icon: 'ri-alert-line' },
];

export interface Endpoint {
  id: string;
  method: 'POST' | 'GET';
  path: string;
  title: string;
  summary: string;
  auth: string;
  request: string | null;
  response: string;
  curl: string;
  js: string;
}

export const endpoints: Endpoint[] = [
  {
    id: 'loyalty-member',
    method: 'POST',
    path: '/loyalty-member',
    title: 'Create or update a member',
    summary:
      'Registers a member from your platform, or updates their profile if they already exist. Returns the member\u2019s ID, which you\u2019ll use for all earn, balance, and redeem calls.',
    auth: 'Bearer API key',
    request: `{
  "external_id": "user_48213",
  "full_name": "Ava Thompson",
  "email": "ava.thompson@bloomcommerce.io",
  "platform": "BloomCommerce"
}`,
    response: `{
  "id": "mem_8f3k2",
  "external_id": "user_48213",
  "full_name": "Ava Thompson",
  "email": "ava.thompson@bloomcommerce.io",
  "tier": "Starter",
  "points_balance": 200,
  "lifetime_points": 200
}`,
    curl: `curl -X POST ${baseUrl}/loyalty-member \\
  -H "Authorization: Bearer lb_live_9f3k2..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "user_48213",
    "full_name": "Ava Thompson",
    "email": "ava.thompson@bloomcommerce.io",
    "platform": "BloomCommerce"
  }'`,
    js: `const res = await fetch("${baseUrl}/loyalty-member", {
  method: "POST",
  headers: {
    Authorization: "Bearer lb_live_9f3k2...",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    external_id: "user_48213",
    full_name: "Ava Thompson",
    email: "ava.thompson@bloomcommerce.io",
    platform: "BloomCommerce",
  }),
});

const member = await res.json();`,
  },
  {
    id: 'loyalty-earn',
    method: 'POST',
    path: '/loyalty-earn',
    title: 'Award points',
    summary:
      'Grants points to a member for any action \u2014 a purchase, referral, or milestone. The ledger entry is immutable and the member\u2019s tier is recalculated automatically.',
    auth: 'Bearer API key (scope: earn)',
    request: `{
  "member_id": "mem_8f3k2",
  "points": 150,
  "reason": "purchase_completed",
  "external_ref": "order_48213"
}`,
    response: `{
  "transaction_id": "TXN-10242",
  "member_id": "mem_8f3k2",
  "points": 150,
  "balance_after": 350,
  "tier": "Pro"
}`,
    curl: `curl -X POST ${baseUrl}/loyalty-earn \\
  -H "Authorization: Bearer lb_live_9f3k2..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "member_id": "mem_8f3k2",
    "points": 150,
    "reason": "purchase_completed"
  }'`,
    js: `const res = await fetch("${baseUrl}/loyalty-earn", {
  method: "POST",
  headers: {
    Authorization: "Bearer lb_live_9f3k2...",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    member_id: "mem_8f3k2",
    points: 150,
    reason: "purchase_completed",
  }),
});

const result = await res.json();`,
  },
  {
    id: 'loyalty-redeem',
    method: 'POST',
    path: '/loyalty-redeem',
    title: 'Redeem a reward',
    summary:
      'Redeems a reward on a member\u2019s behalf. Depending on your redemption policy, the request is either fulfilled instantly or queued for manual review in the dashboard.',
    auth: 'Bearer API key (scope: redeem)',
    request: `{
  "member_id": "mem_8f3k2",
  "reward_id": "rwd-01",
  "quantity": 1
}`,
    response: `{
  "redemption_id": "RDM-2042",
  "status": "pending",
  "points_spent": 1200,
  "balance_after": 650
}`,
    curl: `curl -X POST ${baseUrl}/loyalty-redeem \\
  -H "Authorization: Bearer lb_live_9f3k2..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "member_id": "mem_8f3k2",
    "reward_id": "rwd-01",
    "quantity": 1
  }'`,
    js: `const res = await fetch("${baseUrl}/loyalty-redeem", {
  method: "POST",
  headers: {
    Authorization: "Bearer lb_live_9f3k2...",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    member_id: "mem_8f3k2",
    reward_id: "rwd-01",
    quantity: 1,
  }),
});

const result = await res.json();`,
  },
  {
    id: 'loyalty-balance',
    method: 'GET',
    path: '/loyalty-balance',
    title: 'Read a member\u2019s balance',
    summary:
      'Returns a member\u2019s current redeemable balance, lifetime points, and tier. Use this to surface a live balance in your own UI.',
    auth: 'Bearer API key (scope: read)',
    request: null,
    response: `{
  "id": "mem_8f3k2",
  "points_balance": 650,
  "lifetime_points": 4150,
  "tier": "Pro"
}`,
    curl: `curl "${baseUrl}/loyalty-balance?member_id=mem_8f3k2" \\
  -H "Authorization: Bearer lb_live_9f3k2..."`,
    js: `const res = await fetch(
  "${baseUrl}/loyalty-balance?member_id=mem_8f3k2",
  {
    headers: {
      Authorization: "Bearer lb_live_9f3k2...",
    },
  }
);

const balance = await res.json();`,
  },
  {
    id: 'loyalty-rewards',
    method: 'GET',
    path: '/loyalty-rewards',
    title: 'List active rewards',
    summary:
      'Returns every active reward with its points cost and remaining stock, so you can render your own catalog or a redemption widget.',
    auth: 'Bearer API key (scope: read)',
    request: null,
    response: `{
  "rewards": [
    {
      "id": "rwd-01",
      "name": "Specialty Coffee Voucher",
      "points_cost": 1200,
      "stock": 88,
      "category": "Vouchers"
    }
  ]
}`,
    curl: `curl "${baseUrl}/loyalty-rewards" \\
  -H "Authorization: Bearer lb_live_9f3k2..."`,
    js: `const res = await fetch("${baseUrl}/loyalty-rewards", {
  headers: {
    Authorization: "Bearer lb_live_9f3k2...",
  },
});

const data = await res.json();`,
  },
];

export const errorCodes = [
  { code: '400', title: 'Bad request', detail: 'Malformed JSON or a missing required field.' },
  { code: '401', title: 'Unauthorized', detail: 'Missing, invalid, or revoked API key.' },
  { code: '403', title: 'Forbidden', detail: 'The key lacks the scope required for this endpoint.' },
  { code: '404', title: 'Not found', detail: 'The member or reward does not exist in this program.' },
  { code: '409', title: 'Insufficient balance', detail: 'The member does not have enough points to redeem.' },
  { code: '429', title: 'Rate limited', detail: 'Too many requests. Back off and retry after the reset window.' },
];

export const rateLimits = [
  { plan: 'Starter', requests: '60 req / min', burst: '1,000 / day' },
  { plan: 'Growth', requests: '600 req / min', burst: '100,000 / day' },
  { plan: 'Scale', requests: 'Custom', burst: 'Unlimited' },
];